/* eslint-disable no-restricted-imports, import/order */
import * as z from 'zod'
import { de, en } from 'zod/locales'
/* eslint-enable no-restricted-imports, import/order */

import isVAT, { VATCountryCode } from 'validator/lib/isVAT'

import { NullObjectId, nullObjectIdString, ObjectId } from '@/utils/objectid'
import { isBic } from '@/utils/misc/isBic'
import { isSepaMandateId } from '@/utils/misc/isSepaMandateId'
import { validateIsDateOnly } from '@/utils/date/dateOnly'

export const zodLocales = { de, en }

//
// Unique array (simple types only otherwise uses .toString())
//

type UniqueArrayProps<T extends z.ZodType> = {
    min?: number
    max?: number
    extend?: (type: z.ZodArray<T>) => z.ZodArray<T>
}

export const uniqueArray = <T extends z.ZodType>(schema: T, { min, max, extend }: UniqueArrayProps<T> = {}) => {
    let arrayType = z.array(schema)

    if (min) {
        arrayType = arrayType.min(min)
    }

    if (max) {
        arrayType = arrayType.max(max)
    }

    if (extend) {
        arrayType = extend(arrayType)
    }

    return arrayType.refine((items) => new Set(items.map((x) => (x as any).toString())).size === items.length, {
        message: 'List contains duplicated items',
    })
}

//
// MongoDB ObjectId validator
//
const createNullableObjectIdString = (zodString?: z.ZodString) =>
    (zodString || z.string())
        .transform((value, ctx) => {
            try {
                if (value === nullObjectIdString) {
                    return value
                }
                return new ObjectId(value) as ObjectId | NullObjectId
            } catch {
                ctx.issues.push({
                    code: 'custom',
                    input: value,
                    params: {
                        i18n: { key: 'Not a valid ObjectId' },
                    },
                })
                return z.NEVER
            }
        })
        .meta({ override: { type: 'string' } })

const createObjectIdString = (zodString?: z.ZodString) =>
    (zodString || z.string())
        .transform((value, ctx) => {
            try {
                if (value === nullObjectIdString) {
                    throw new Error('NullObjectId is not allowed')
                }
                return new ObjectId(value) as ObjectId
            } catch {
                ctx.issues.push({
                    code: 'custom',
                    input: value,
                    params: {
                        i18n: { key: 'Not a valid ObjectId' },
                    },
                })
                return z.NEVER
            }
        })
        .meta({ override: { type: 'string' } })

export const nullableObjectIdString = createNullableObjectIdString()

export const objectIdString = createObjectIdString()

const createObjectId = () =>
    z
        .instanceof(ObjectId)
        .meta({
            // Tell zod-openapi how to represent this in the OpenAPI spec
            effectType: 'output',
            type: 'string',
            format: 'ObjectId',
            description: 'ObjectId as string',
        })
        .or(objectIdString)
        .meta({
            // Tell zod-openapi how to represent this in the OpenAPI spec
            effectType: 'output',
            type: 'string',
            format: 'ObjectId',
            description: 'ObjectId as string',
        })

export const objectId = createObjectId()

//
// RRule validator
//

export const rrule = (mustEnd: boolean, zodString?: z.ZodString) =>
    (zodString || z.string()).check(async ({ value, ...ctx }) => {
        try {
            const RRule = (await import('rrule')).RRule

            const rule = RRule.fromString(value)

            if (mustEnd) {
                if (!rule.origOptions.count && !rule.origOptions.until) {
                    ctx.issues.push({
                        code: 'custom',
                        input: value,
                        params: {
                            i18n: { key: 'custom.rruleMustEnd' },
                        },
                    })
                }
            }
        } catch {
            ctx.issues.push({
                code: 'custom',
                input: value,
                params: {
                    i18n: { key: 'custom.invalidRRule' },
                },
            })
        }
    })

//
// Cron validator
//

const HOURLY_INTERVAL_FIELD_PATTERN = /^\*\/([1-9]|1\d|2[0-3])$/
const MINUTE_PATTERN = /^(?:[0-9]|[1-5][0-9])$/
const HOUR_PATTERN = /^(?:[0-9]|1[0-9]|2[0-3])$/
const DAY_OF_MONTH_PATTERN = /^(?:[1-9]|[12][0-9]|3[01])$/
const DAY_OF_WEEK_LIST_PATTERN = /^(?:[0-6])(?:,[0-6])*$/

export const isValidCronExpression = (value: string) => {
    const fields = value.trim().split(/\s+/)

    if (fields.length !== 5) {
        return false
    }

    const [minute, hour, dayOfMonth, month, dayOfWeek] = fields

    if (!MINUTE_PATTERN.test(minute) || month !== '*') {
        return false
    }

    if (hour === '*' && dayOfMonth === '*' && dayOfWeek === '*') {
        return true
    }

    if (HOURLY_INTERVAL_FIELD_PATTERN.test(hour) && dayOfMonth === '*' && dayOfWeek === '*') {
        return true
    }

    if (!HOUR_PATTERN.test(hour)) {
        return false
    }

    if (dayOfMonth === '*' && dayOfWeek === '*') {
        return true
    }

    if (dayOfMonth === '*' && DAY_OF_WEEK_LIST_PATTERN.test(dayOfWeek)) {
        return new Set(dayOfWeek.split(',')).size === dayOfWeek.split(',').length
    }

    if (DAY_OF_MONTH_PATTERN.test(dayOfMonth) && dayOfWeek === '*') {
        return true
    }

    return false
}

export const cron = (zodString?: z.ZodString) =>
    (zodString || z.string()).check(({ value, ...ctx }) => {
        if (!isValidCronExpression(value)) {
            ctx.issues.push({
                code: 'custom',
                input: value,
                params: {
                    i18n: { key: 'custom.invalidCron' },
                },
            })
        }
    })

//
// Phone validator and transformer, ensures final format is in E.164
//

export const phone = (zodString?: z.ZodString) =>
    (zodString || z.string())
        .transform(async (value: string, ctx) => {
            try {
                const { formatPhone } = await import('@/utils/misc/phone')
                return formatPhone(value, 'E.164')
            } catch {
                ctx.issues.push({
                    code: 'custom',
                    input: value,
                    params: {
                        i18n: { key: 'custom.invalidPhone' },
                    },
                })

                return z.NEVER
            }
        })
        .meta({ override: { type: 'string', format: 'phone' } })

// TODO : Currently this is an alias to generic phone due trying to avoid
// using the big data library from libphone so we currently don't really know if
// the given phone is a valid mobile or just a regular phone
export const mobilePhone = phone

//
// Tax-ID validator. Because we require the country we expect tax-ids
// to be prefixed by the the country like DE_36574261809 however we'll
// normalize that and will return the tax id without the country specifier then.
//

export const taxID = (zodString?: z.ZodString) =>
    (zodString || z.string()).transform((value: string, ctx) => {
        const country = (value || '').substring(0, 2)
        const taxID = (value || '').substring(2).replace(/\D/g, '')

        if (country && taxID) {
            //
            // This is ugly as fuck we need to fix this one day
            //

            let isValid = false

            switch (country) {
                case 'AT':
                    // TODO : This is not a real validation
                    isValid = taxID.length === 9 && /\d+/.test(taxID)
                    break
                case 'DE':
                case 'LU':
                case 'US':
                    // TODO : This is not a real validation
                    isValid = [10, 11].includes(taxID.length) && /\d+/.test(taxID)
                    break
                default:
                    throw new Error(`Unsupported country "${country}" for isTaxID`)
            }

            if (isValid) {
                return taxID
            }
        }

        ctx.issues.push({
            code: 'custom',
            input: value,
            params: {
                i18n: { key: 'custom.invalidTaxID' },
            },
        })

        return z.NEVER
    })

//
// VAT validator
//

export const VATID = (zodString?: z.ZodString) =>
    (zodString || z.string()).check(({ value, ...ctx }) => {
        if (value) {
            const country = value.substring(0, 2)

            try {
                if (isVAT(value, country as VATCountryCode)) {
                    return undefined // ok
                }
            } catch {
                // noop
            }
        }

        ctx.issues.push({
            code: 'custom',
            input: value,
            params: {
                i18n: { key: 'custom.invalidVATID' },
            },
        })
    })

//
// Creditor Identifier validator
//

const CREDITOR_IDENTIFIER_REGEX =
    // eslint-disable-next-line no-useless-escape, max-len
    /[a-zA-Z]{2,2}[0-9]{2,2}([A-Za-z0-9]|[\+|\?|\/|\-|\:|\(|\)|\.|,|']){3,3}([A-Za-z0-9]|[\+|\?|\/|\-|:|\(|\)|\.|,|']){1,28}/

const CREDITOR_IDENTIFIER_DIGITS: Record<string, number> = {
    AT: 16,
    DE: 16,
    LU: 16,
    // TODO : Extend for other countries
}

export const creditorIdentifier = (zodString?: z.ZodString) =>
    (zodString || z.string()).check(({ value, ...ctx }) => {
        const match = value.match(CREDITOR_IDENTIFIER_REGEX)

        if (match) {
            const digits = CREDITOR_IDENTIFIER_DIGITS[value.substring(0, 2)] || undefined
            if (digits === undefined || value.length === digits + 2) {
                return undefined // ok
            }
        }

        ctx.issues.push({
            code: 'custom',
            input: value,
            params: {
                i18n: { key: 'custom.invalidCreditorIdentifier' },
            },
        })
    })

//
// BIC validator
//

export const bic = (zodString?: z.ZodString) =>
    (zodString || z.string()).check(({ value, ...ctx }) => {
        if (!isBic(value)) {
            ctx.issues.push({
                code: 'custom',
                input: value,
                params: {
                    i18n: { key: 'custom.invalidBIC' },
                },
            })
        }
    })

//
// IBAN validator
//

export const iban = (zodString?: z.ZodString) =>
    (zodString || z.string())
        .transform((value) => value.replace(/\s/g, ''))
        .check(async ({ value, ...ctx }) => {
            const { isIBAN } = await import('@/utils/misc/isIBAN')

            if (!isIBAN(value)) {
                // if failed, it means the provided iban is not valid
                ctx.issues.push({
                    code: 'custom',
                    input: value,
                    params: {
                        i18n: { key: 'custom.invalidIBAN' },
                    },
                })
            }
        })
        .meta({ override: { type: 'string', format: 'iban' } })

//
// SEPA Mandate-Id validator
//

export const sepaMandateId = (zodString?: z.ZodString) =>
    (zodString || z.string()).check(({ value, ...ctx }) => {
        if (!isSepaMandateId(value)) {
            ctx.issues.push({
                code: 'custom',
                input: value,
                params: {
                    i18n: { key: 'custom.invalidSepaMandateId' },
                },
            })
        }
    })

//
// JSON
//

const JSONLiteralSchema = z.union([z.string(), z.number(), z.boolean(), z.null()])

type JSONLiteral = z.infer<typeof JSONLiteralSchema>

export type JSON = JSONLiteral | { [key: string]: JSON } | JSON[]

export const JSONSchema: z.ZodType<JSON> = z.lazy(() =>
    z.union([JSONLiteralSchema, z.array(JSONSchema), z.record(z.string(), JSONSchema)])
)
// .openapi({ ref: 'JSON', title: 'JSON' })

export const json = () => JSONSchema

export const jsonString = (zodString?: z.ZodString) =>
    (zodString || z.string()).transform((str, ctx): JSON => {
        try {
            return JSON.parse(str)
        } catch {
            ctx.issues.push({ code: 'custom', message: 'Invalid JSON', input: str })
            return z.NEVER
        }
    })

//
// Date
//

// Minimum date to prevent typo issues on dates like 0225 etc
const MIN_ALLOWED_DATE = new Date(Date.UTC(1800, 0, 1))
const MAX_ALLOWED_DATE = new Date(Date.UTC(2100, 0, 1))

// Since Zod 4.6 a preprocess counts as optional on input, which carries through `.nullable()` and makes zod-openapi list
// required nullable dates as optional. A missing key still fails validation, so only this flag is restored.
const preprocessDate: typeof z.preprocess = (fn, schema) => {
    const preprocess = z.preprocess(fn, schema)

    preprocess.def.in._zod.optin = undefined

    return preprocess
}

const createIsoDateTime = (preserveMilliseconds: boolean) =>
    preprocessDate(
        (arg: Date) => {
            // fail straight away if null date intead of giving epoch date 1970-01-01T00:00:00.000Z
            // @ts-expect-error its fine
            if (arg === null || arg === 0) {
                return undefined
            }
            return arg
        },
        z.coerce
            .date()
            .transform((date) => {
                if (!preserveMilliseconds) date.setUTCMilliseconds(0)

                return date
            })
            .check(({ value, issues }) => {
                if (value.getTime() < MIN_ALLOWED_DATE.getTime()) {
                    issues.push({
                        code: 'custom',
                        input: value,
                        params: {
                            i18n: { key: 'custom.invalidMinDate' },
                        },
                    })
                }
                if (value.getTime() > MAX_ALLOWED_DATE.getTime()) {
                    issues.push({
                        code: 'custom',
                        input: value,
                        params: {
                            i18n: { key: 'custom.invalidMaxDate' },
                        },
                    })
                }
            })
            .meta({ override: { type: 'string', format: 'date-time', example: '2026-06-15T22:30:00Z' } })
    )

export const isoDateTime = createIsoDateTime(false)
export const isoDateTimeWithMilliseconds = createIsoDateTime(true)

export const isoDateOnly = preprocessDate(
    (arg: Date) => {
        // fail straight away if null date intead of giving epoch date 1970-01-01T00:00:00.000Z
        // @ts-expect-error its fine
        if (arg === null || arg === 0) {
            return undefined
        }
        return arg
    },
    z.coerce.date().check(({ value, issues }) => {
        if (!validateIsDateOnly(value)) {
            issues.push({
                code: 'custom',
                input: value,
                params: {
                    i18n: { key: 'custom.invalidDateOnly' },
                },
            })
        }
        if (value.getTime() < MIN_ALLOWED_DATE.getTime()) {
            issues.push({
                code: 'custom',
                input: value,
                params: {
                    i18n: { key: 'custom.invalidMinDate' },
                },
            })
        }
        if (value.getTime() > MAX_ALLOWED_DATE.getTime()) {
            issues.push({
                code: 'custom',
                input: value,
                params: {
                    i18n: { key: 'custom.invalidMaxDate' },
                },
            })
        }
    })
)

// export type inDateOnly = z.input<typeof isoDateOnly>
// export type outDateOnly = z.output<typeof isoDateOnly>

//
// Hex-Color
//

// We don't use validator/isHexColor because we only want to support 6-digits regular hex colors
// as thats most compatible with all formats

const HEX_COLOR_REGEX = /^#[0-9A-F]{6}$/i

export const hexColor = (zodString?: z.ZodString) => (zodString || z.string()).regex(HEX_COLOR_REGEX)

//
// Hex-Color with (optional) alpha channel
//

const HEX_COLOR_ALPHA_REGEX = /^#[0-9A-F]{6,8}$/i

export const hexColorAlpha = (zodString?: z.ZodString) => (zodString || z.string()).regex(HEX_COLOR_ALPHA_REGEX)

export const getZodKeys = (schema: z.core.$ZodType): string[] => {
    if (schema === null || schema === undefined) {
        return []
    }

    if (schema instanceof z.ZodObject) {
        return Object.keys(schema.shape)
    }

    if (schema instanceof z.ZodDiscriminatedUnion) {
        return [...new Set(schema.options.flatMap((option) => getZodKeys(option)))]
    }

    if (schema instanceof z.ZodPipe) {
        return getZodKeys(schema.def.in)
    }

    if (schema instanceof z.ZodNullable || schema instanceof z.ZodOptional || schema instanceof z.ZodDefault) {
        return getZodKeys(schema.unwrap())
    }

    if (schema instanceof z.ZodArray) {
        return getZodKeys(schema.element)
    }

    return []
}

export const recordWithRequiredKeys = <K extends Record<string, any>, V extends z.ZodType>(
    keysSchema: z.ZodEnum<K>,
    valueSchema: V
) =>
    z
        .record(keysSchema, valueSchema)
        // this makes the validation
        .check((ctx) => {
            keysSchema.options.forEach((key) => {
                const result = valueSchema.safeParse(ctx.value[key])
                if (!result.success) {
                    result.error.issues.forEach((error) =>
                        ctx.issues.push({
                            ...error,
                            input: ctx.value[key] as any,
                            path: [key, ...error.path],
                        })
                    )
                }
            })
        })
// this is just to infer the right type
// .refine((val): val is Record<K, z.infer<V>> => true)

// Misc schemas
export const singleLineString = (zodString?: z.ZodString) =>
    (zodString || z.string()).transform((value) => value.replace(/(?:\r\n|\r|\n)/g, ' '))

// We needed to add a customized email validation since zod currently does not accept umlauts
// https://github.com/colinhacks/zod/pull/3678
export const emailRegexUtf8Extended =
    /^(?!.*[@]{2})(?!.*[.]{2})(?!.*[.@]$)[\p{L}\p{N}!#$%&'*+/=?^_{|}~-](?:[\p{L}\p{N}!#$%&'*+/=?^_{|}~.-]*[\p{L}\p{N}!#$%&'*+/=?^_{|}~-])?@[\p{L}\p{N}][\p{L}\p{N}-]*?(\.[\p{L}\p{N}-]+)*\.[\p{L}]{2,}$/u

export const email = (zodString?: z.ZodString) =>
    (zodString || z.string()).check(({ value, ...ctx }) => {
        if (!emailRegexUtf8Extended.test(value)) {
            ctx.issues.push({
                code: 'custom',
                input: value,
                params: {
                    i18n: { key: 'custom.invalidEmail' },
                },
            })
        }
    })

//
// Domain name
//

const DOMAIN_NAME_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/

export const domainName = (zodString?: z.ZodString) => (zodString || z.string()).regex(DOMAIN_NAME_REGEX)

//
// merge function that handle up to 3 merges at once
//

export function merge<S1 extends z.ZodRawShape, S2 extends z.ZodRawShape>(o1: z.ZodObject<S1>, o2: z.ZodObject<S2>) {
    const mergedShape: S1 & S2 = {
        ...o1.shape,
        ...o2.shape,
    }

    return z.object(mergedShape).strict()
}

export function merge3<S1 extends z.ZodRawShape, S2 extends z.ZodRawShape, S3 extends z.ZodRawShape>(
    o1: z.ZodObject<S1>,
    o2: z.ZodObject<S2>,
    o3: z.ZodObject<S3>
) {
    const mergedShape: S1 & S2 & S3 = {
        ...o1.shape,
        ...o2.shape,
        ...o3.shape,
    }

    return z.object(mergedShape).strict()
}

export const regex = (flags?: string) =>
    z
        .string()
        .min(1)
        .refine(
            (val) => {
                try {
                    new RegExp(val, flags)
                } catch (error) {
                    if (error instanceof SyntaxError) {
                        return false
                    }
                }

                return true
            },
            {
                params: {
                    i18n: { key: 'custom.invalidRegex' },
                },
            }
        )

/**
 * this is a hacky function to simplify the type inference of zod schemas
 */
export const toZodType = <Schema extends z.ZodType<any, any>>(schema: Schema) => schema as z.ZodType<z.infer<Schema>>
